import { useState } from 'react';
import { Loader2, Play, UserRound } from 'lucide-react';

/**
 * Simulate approval path for a dummy submitter (person number) against the structured design.
 */
export default function BpmSimulatePanel({
  design,
  onResult,
  disabled = false,
  api,
}) {
  const [personNumber, setPersonNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function runSimulation() {
    const pn = personNumber.trim();
    if (!pn || !design || loading) return;
    setLoading(true);
    setError('');
    setResult(null);
    onResult?.(null);
    try {
      const res = await api.post('/api/copilot/bpm/simulate-path', {
        personNumber: pn,
        design,
      });
      setResult(res.data);
      onResult?.(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Path simulation failed';
      setError(msg);
      const partial = err.response?.data;
      if (partial) {
        setResult(partial);
        onResult?.(partial);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-900">
            Simulate approval path
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label
          className="flex flex-col gap-0.5 text-[11px] text-slate-600 min-w-[140px]"
          title="Resolves worker from Fusion and highlights the predicted path on the canvas"
        >
          Person number
          <input
            type="text"
            value={personNumber}
            onChange={(e) => setPersonNumber(e.target.value)}
            disabled={disabled || loading || !design}
            placeholder="e.g. 51001148"
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSimulation();
            }}
          />
        </label>
        <button
          type="button"
          onClick={runSimulation}
          disabled={disabled || loading || !design || !personNumber.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-800 disabled:bg-slate-300"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          Run simulation
        </button>
      </div>

      {error && (
        <p className="text-xs text-rose-800 rounded-md bg-rose-50 border border-rose-100 px-2 py-1.5">
          {error}
        </p>
      )}

      {result?.worker && (result.worker.displayName || result.worker.personNumber) && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-100 bg-white/80 px-2 py-1.5 text-xs text-slate-800">
          <UserRound className="h-4 w-4 shrink-0 text-emerald-700 mt-0.5" />
          <div>
            <p className="font-medium text-emerald-900">
              {result.worker.displayName || 'Worker'}{' '}
              <span className="font-normal text-slate-600">({result.worker.personNumber})</span>
            </p>
            {result.summary && <p className="text-[11px] text-slate-600 mt-0.5">{result.summary}</p>}
            {result.matched === false && (
              <p className="text-[11px] text-amber-800 mt-0.5">Conditions did not match — path not highlighted.</p>
            )}
          </div>
        </div>
      )}

      {result?.path?.length > 0 && (
        <ol className="list-decimal pl-4 space-y-1 text-[11px] text-slate-700">
          {result.path.map((stage) => (
            <li key={stage.stageId || stage.stageOrder}>
              <span className="font-medium">Stage {stage.stageOrder}</span>
              {stage.routing === 'parallel' ? ' (parallel)' : ''}:{' '}
              {(stage.approvers || [])
                .map((a) => `${a.name}${a.personNumber ? ` #${a.personNumber}` : ''}`)
                .join(' → ')}
            </li>
          ))}
        </ol>
      )}

      {(result?.warnings || []).length > 0 && (
        <ul className="text-[10px] text-amber-900 space-y-0.5 list-disc pl-4 max-h-28 overflow-y-auto">
          {result.warnings.slice(0, 12).map((w, i) => (
            <li key={i}>{w}</li>
          ))}
          {result.warnings.length > 12 && (
            <li className="list-none text-slate-500">+{result.warnings.length - 12} more…</li>
          )}
        </ul>
      )}

      {result?.limits?.length > 0 && (
        <p className="text-[10px] text-slate-500 border-t border-emerald-100 pt-1.5">
          v1 limits: {result.limits[0]}
        </p>
      )}
    </div>
  );
}
